import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Calculator, Settings } from 'lucide-react';
import { CantonSelector } from '@/components/canton/CantonSelector';
import { InsuranceSelector } from '@/components/insurance/InsuranceSelector';
import { VacationSettings } from '@/components/vacation/VacationSettings';
import { CantonProvider, useCantonContext } from '@/contexts/CantonContext';
import type { Canton, InsuranceWithRate } from '@/types/insurance';
import { DEFAULT_VACATION_PERCENTAGE_4W } from '@/config/constants';

interface SwissPayrollSectionProps {
  // Employee data
  employeeAge?: number;
  monthlySalary?: number;
  hourlySalary?: number;
  paymentModel?: 'monthly' | 'hourly';
  
  // Current values
  selectedCanton?: Canton;
  selectedInsurances?: InsuranceWithRate[];
  vacationModel?: 'fixed_days' | 'percentage_included';
  annualVacationDays?: number;
  vacationPercentage?: number;
  
  // Callbacks
  onCantonChange?: (canton: Canton) => void;
  onInsurancesChange?: (insurances: InsuranceWithRate[]) => void;
  onVacationModelChange?: (model: 'fixed_days' | 'percentage_included') => void;
  onAnnualVacationDaysChange?: (days: number) => void;
  onVacationPercentageChange?: (percentage: number) => void;
  
  className?: string;
}

const SwissPayrollSectionContent: React.FC<SwissPayrollSectionProps> = ({
  employeeAge,
  monthlySalary,
  hourlySalary,
  paymentModel = 'monthly',
  selectedCanton,
  selectedInsurances = [],
  vacationModel = 'fixed_days',
  annualVacationDays = 20,
  vacationPercentage = DEFAULT_VACATION_PERCENTAGE_4W,
  onCantonChange,
  onInsurancesChange,
  onVacationModelChange,
  onAnnualVacationDaysChange,
  onVacationPercentageChange,
  className
}) => {
  const { 
    selectedCanton: contextCanton, 
    setSelectedCanton, 
    getInsurancesWithDefaults,
    isLoading 
  } = useCantonContext();

  const [localInsurances, setLocalInsurances] = useState<InsuranceWithRate[]>(selectedInsurances);

  // Sync with context canton
  useEffect(() => {
    if (selectedCanton && selectedCanton !== contextCanton) {
      setSelectedCanton(selectedCanton);
    }
  }, [selectedCanton, contextCanton, setSelectedCanton]);

  // Update insurances when canton changes or employee data changes
  useEffect(() => {
    if (contextCanton && !isLoading) {
      const salary = paymentModel === 'monthly' ? monthlySalary : hourlySalary;
      const defaultInsurances = getInsurancesWithDefaults(employeeAge, salary);
      
      // Merge with existing selections, preserving custom rates
      const mergedInsurances = defaultInsurances.map(defaultIns => {
        const existing = localInsurances.find(local => local.id === defaultIns.id);
        if (existing) {
          return {
            ...defaultIns,
            is_enabled: existing.is_enabled,
            rate_percent: existing.is_custom_rate ? existing.rate_percent : defaultIns.rate_percent,
            is_custom_rate: existing.is_custom_rate
          };
        }
        return defaultIns;
      });
      
      setLocalInsurances(mergedInsurances);
      onInsurancesChange?.(mergedInsurances);
    }
  }, [contextCanton, employeeAge, monthlySalary, hourlySalary, paymentModel, isLoading]);

  const handleCantonChange = (canton: Canton) => {
    setSelectedCanton(canton);
    onCantonChange?.(canton);
  };

  const handleInsuranceChange = (insurances: InsuranceWithRate[]) => {
    setLocalInsurances(insurances);
    onInsurancesChange?.(insurances);
  };

  const handleResetToDefaults = () => {
    if (contextCanton) {
      const salary = paymentModel === 'monthly' ? monthlySalary : hourlySalary;
      const defaultInsurances = getInsurancesWithDefaults(employeeAge, salary);
      setLocalInsurances(defaultInsurances);
      onInsurancesChange?.(defaultInsurances);
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Schweizer Lohnabrechnung
          {isLoading && <Badge variant="outline">Lädt...</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Canton Selection */}
        <CantonSelector
          selectedCanton={contextCanton}
          onCantonChange={handleCantonChange}
          onResetToDefaults={handleResetToDefaults}
          showResetButton={true}
        />

        <Separator />

        {/* Insurance Selection */}
        {contextCanton && !isLoading && (
          <InsuranceSelector
            insurances={getInsurancesWithDefaults(employeeAge, paymentModel === 'monthly' ? monthlySalary : hourlySalary)}
            selectedInsurances={localInsurances}
            onInsuranceChange={handleInsuranceChange}
            employeeAge={employeeAge}
          />
        )}

        <Separator />

        {/* Vacation Settings */}
        <VacationSettings
          vacationModel={vacationModel}
          annualDays={annualVacationDays}
          percentageRate={vacationPercentage}
          onVacationModelChange={onVacationModelChange || (() => {})}
          onAnnualDaysChange={onAnnualVacationDaysChange || (() => {})}
          onPercentageRateChange={onVacationPercentageChange || (() => {})}
        />

        {/* Summary Card */}
        {localInsurances.filter(i => i.is_enabled).length > 0 && (
          <div className="bg-accent/50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="h-4 w-4" />
              <h4 className="font-medium text-sm">Zusammenfassung</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium">Kanton:</p>
                <p className="text-muted-foreground">{contextCanton}</p>
              </div>
              
              <div>
                <p className="font-medium">Versicherungen:</p>
                <p className="text-muted-foreground">
                  {localInsurances.filter(i => i.is_enabled).length} aktiv
                </p>
              </div>
              
              <div>
                <p className="font-medium">Ferien-Modell:</p>
                <p className="text-muted-foreground">
                  {vacationModel === 'fixed_days' 
                    ? `${annualVacationDays} Tage/Jahr` 
                    : `${vacationPercentage}% im Lohn`}
                </p>
              </div>
              
              <div>
                <p className="font-medium">Total Abzüge:</p>
                <p className="text-muted-foreground font-mono">
                  {localInsurances
                    .filter(i => i.is_enabled)
                    .reduce((sum, i) => sum + i.rate_percent, 0)
                    .toFixed(2)}%
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const SwissPayrollSection: React.FC<SwissPayrollSectionProps> = (props) => {
  return (
    <CantonProvider defaultCanton={props.selectedCanton || 'ZH'}>
      <SwissPayrollSectionContent {...props} />
    </CantonProvider>
  );
};